import { z } from 'zod';

import { ApiError } from '../api/client';
import type {
  Allergen,
  AllergenFormInput,
  AllergenUpdateInput,
  DietaryTag,
  DietaryTagFormInput,
  DietaryTagUpdateInput,
  MenuPosition,
  MenuPositionFormInput,
  MenuPositionUpdateInput,
  ServiceType,
  ServiceTypeFormInput,
  ServiceTypeUpdateInput,
} from './api';

const displayOrderSchema = z
  .string()
  .trim()
  .refine((value) => value === '' || (Number.isInteger(Number(value)) && Number(value) >= 0), {
    message: 'El orden debe ser un número entero mayor o igual a 0',
  })
  .transform((value) => (value === '' ? 0 : Number(value)));

/**
 * `code` is immutable once created (`apps/api` `updateServiceTypeSchema`/
 * `updateDietaryTagSchema`/`updateAllergenSchema` have no `code` field): the
 * field stays in the schema so the edit form can show it read-only, but
 * `toServiceTypeUpdateInput`/`toDietaryTagUpdateInput`/`toAllergenUpdateInput`
 * never send it.
 */
export const serviceTypeFormSchema = z.object({
  code: z.string().trim().min(1, 'El código es obligatorio').max(50),
  name: z.string().trim().min(1, 'El nombre es obligatorio').max(200),
  displayOrder: displayOrderSchema,
});

export type ServiceTypeFormValues = z.infer<typeof serviceTypeFormSchema>;
export type ServiceTypeFormFields = { code: string; name: string; displayOrder: string };

export const DEFAULT_SERVICE_TYPE_FORM_VALUES: ServiceTypeFormFields = {
  code: '',
  name: '',
  displayOrder: '',
};

export function toServiceTypeFormValues(serviceType: ServiceType): ServiceTypeFormFields {
  return {
    code: serviceType.code,
    name: serviceType.name,
    displayOrder: String(serviceType.displayOrder),
  };
}

export function toServiceTypeCreateInput(values: ServiceTypeFormValues): ServiceTypeFormInput {
  return { code: values.code, name: values.name, displayOrder: values.displayOrder };
}

export function toServiceTypeUpdateInput(
  current: ServiceType,
  values: ServiceTypeFormValues,
): ServiceTypeUpdateInput {
  const changes: ServiceTypeUpdateInput = {};

  if (values.name !== current.name) changes.name = values.name;
  if (values.displayOrder !== current.displayOrder) changes.displayOrder = values.displayOrder;

  return changes;
}

export const dietaryTagFormSchema = z.object({
  code: z.string().trim().min(1, 'El código es obligatorio').max(50),
  name: z.string().trim().min(1, 'El nombre es obligatorio').max(200),
  color: z.string().trim().max(20),
  icon: z.string().trim().max(100),
  displayOrder: displayOrderSchema,
});

export type DietaryTagFormValues = z.infer<typeof dietaryTagFormSchema>;
export type DietaryTagFormFields = {
  code: string;
  name: string;
  color: string;
  icon: string;
  displayOrder: string;
};

export const DEFAULT_DIETARY_TAG_FORM_VALUES: DietaryTagFormFields = {
  code: '',
  name: '',
  color: '',
  icon: '',
  displayOrder: '',
};

export function toDietaryTagFormValues(dietaryTag: DietaryTag): DietaryTagFormFields {
  return {
    code: dietaryTag.code,
    name: dietaryTag.name,
    color: dietaryTag.color ?? '',
    icon: dietaryTag.icon ?? '',
    displayOrder: String(dietaryTag.displayOrder),
  };
}

export function toDietaryTagCreateInput(values: DietaryTagFormValues): DietaryTagFormInput {
  return {
    code: values.code,
    name: values.name,
    displayOrder: values.displayOrder,
    ...(values.color === '' ? {} : { color: values.color }),
    ...(values.icon === '' ? {} : { icon: values.icon }),
  };
}

export function toDietaryTagUpdateInput(
  current: DietaryTag,
  values: DietaryTagFormValues,
): DietaryTagUpdateInput {
  const changes: DietaryTagUpdateInput = {};

  if (values.name !== current.name) changes.name = values.name;
  if (values.displayOrder !== current.displayOrder) changes.displayOrder = values.displayOrder;

  const newColor = values.color === '' ? null : values.color;
  if (newColor !== current.color) changes.color = newColor;

  const newIcon = values.icon === '' ? null : values.icon;
  if (newIcon !== current.icon) changes.icon = newIcon;

  return changes;
}

export const allergenFormSchema = z.object({
  code: z.string().trim().min(1, 'El código es obligatorio').max(50),
  name: z.string().trim().min(1, 'El nombre es obligatorio').max(200),
  icon: z.string().trim().max(100),
  displayOrder: displayOrderSchema,
});

export type AllergenFormValues = z.infer<typeof allergenFormSchema>;
export type AllergenFormFields = { code: string; name: string; icon: string; displayOrder: string };

export const DEFAULT_ALLERGEN_FORM_VALUES: AllergenFormFields = {
  code: '',
  name: '',
  icon: '',
  displayOrder: '',
};

export function toAllergenFormValues(allergen: Allergen): AllergenFormFields {
  return {
    code: allergen.code,
    name: allergen.name,
    icon: allergen.icon ?? '',
    displayOrder: String(allergen.displayOrder),
  };
}

export function toAllergenCreateInput(values: AllergenFormValues): AllergenFormInput {
  return {
    code: values.code,
    name: values.name,
    displayOrder: values.displayOrder,
    ...(values.icon === '' ? {} : { icon: values.icon }),
  };
}

export function toAllergenUpdateInput(
  current: Allergen,
  values: AllergenFormValues,
): AllergenUpdateInput {
  const changes: AllergenUpdateInput = {};

  if (values.name !== current.name) changes.name = values.name;
  if (values.displayOrder !== current.displayOrder) changes.displayOrder = values.displayOrder;

  const newIcon = values.icon === '' ? null : values.icon;
  if (newIcon !== current.icon) changes.icon = newIcon;

  return changes;
}

export const menuPositionFormSchema = z.object({
  code: z.string().trim().min(1, 'El código es obligatorio').max(50),
  name: z.string().trim().min(1, 'El nombre es obligatorio').max(200),
  serviceTypeId: z
    .string()
    .min(1, 'Seleccioná un tipo de servicio')
    .uuid({ message: 'Seleccioná un tipo de servicio' }),
  displayOrder: displayOrderSchema,
});

export type MenuPositionFormValues = z.infer<typeof menuPositionFormSchema>;
export type MenuPositionFormFields = {
  code: string;
  name: string;
  serviceTypeId: string;
  displayOrder: string;
};

export const DEFAULT_MENU_POSITION_FORM_VALUES: MenuPositionFormFields = {
  code: '',
  name: '',
  serviceTypeId: '',
  displayOrder: '',
};

export function toMenuPositionFormValues(position: MenuPosition): MenuPositionFormFields {
  return {
    code: position.code,
    name: position.name,
    serviceTypeId: position.serviceTypeId,
    displayOrder: String(position.displayOrder),
  };
}

export function toMenuPositionCreateInput(values: MenuPositionFormValues): MenuPositionFormInput {
  return {
    code: values.code,
    name: values.name,
    serviceTypeId: values.serviceTypeId,
    displayOrder: values.displayOrder,
  };
}

export function toMenuPositionUpdateInput(
  current: MenuPosition,
  values: MenuPositionFormValues,
): MenuPositionUpdateInput {
  const changes: MenuPositionUpdateInput = {};

  if (values.name !== current.name) changes.name = values.name;
  if (values.serviceTypeId !== current.serviceTypeId) changes.serviceTypeId = values.serviceTypeId;
  if (values.displayOrder !== current.displayOrder) changes.displayOrder = values.displayOrder;

  return changes;
}

export type CategoryEntity = 'SERVICE_TYPE' | 'DIETARY_TAG' | 'ALLERGEN' | 'MENU_POSITION';

const DUPLICATE_CODE_BY_ENTITY: Record<CategoryEntity, string> = {
  SERVICE_TYPE: 'SERVICE_TYPE_CODE_DUPLICATE',
  DIETARY_TAG: 'DIETARY_TAG_CODE_DUPLICATE',
  ALLERGEN: 'ALLERGEN_CODE_DUPLICATE',
  MENU_POSITION: 'MENU_POSITION_CODE_DUPLICATE',
};

/**
 * Server errors never have their `message` parsed: the `code` field on the
 * 409/403 body (`CatalogExceptionFilter`) drives the mapping, same
 * convention as `site-form-schema.ts`'s `mapSiteError`. Shared across the
 * three category entities since `CatalogExceptionFilter` uses the same
 * shape for all of them, only the duplicate-code string differs.
 */
export function mapCategoryError(error: unknown, entity: CategoryEntity): string {
  if (error === undefined || error === null) {
    return '';
  }

  if (!(error instanceof ApiError)) {
    return 'Ocurrió un error de conexión. Intentá de nuevo.';
  }

  const code = (error.body as { code?: string } | null)?.code;

  if (error.status === 409 && code === DUPLICATE_CODE_BY_ENTITY[entity]) {
    return 'Ya existe una categoría con ese código.';
  }

  if (error.status === 409 && code === 'CATEGORY_IN_USE') {
    return 'No se puede desactivar: hay al menos un menú no archivado que la usa.';
  }

  if (error.status === 409 && code === 'MENU_POSITION_SERVICE_TYPE_LOCKED') {
    return 'No se puede cambiar el tipo de servicio: la posición está en uso en el calendario o en una plantilla.';
  }

  if (error.status === 403) {
    return 'No tenés permiso para realizar esta acción.';
  }

  if (error.status === 400) {
    return 'Revisá los datos ingresados e intentá de nuevo.';
  }

  return 'Ocurrió un error inesperado. Intentá de nuevo.';
}
